import { useState, useEffect } from 'react';
import {
  Bell,
  UserPlus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Send,
  Play,
  Settings,
  History,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Check,
  Eraser,
} from 'lucide-react';
import {
  useAlertRecipients,
  useAlertRules,
  useSmtpConfig,
  useAlertHistory,
  useUpdateAlertHistoryStatus,
  useCurrentUser,
} from '../hooks/useApi';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import { api } from '../api/client';
import { useQueryClient } from '@tanstack/react-query';
import type {
  AlertRecipient,
  AlertRule,
  AlertSeverity,
  AlertHistoryEntry,
  AlertCheckResult,
  AckStatus,
} from '../types/api';

type Tab = 'recipients' | 'rules' | 'smtp' | 'history';

const SEVERITY_STYLES: Record<AlertSeverity, string> = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

const ACK_STATUS_META: Record<AckStatus, { label: string; badge: string }> = {
  new: { label: 'Nowy', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  acknowledged: { label: 'Potwierdzony', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  cleared: { label: 'Wyczyszczony', badge: 'bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-400' },
};

const STATUS_FILTERS: { value: AckStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Wszystkie' },
  { value: 'new', label: 'Nowe' },
  { value: 'acknowledged', label: 'Potwierdzone' },
  { value: 'cleared', label: 'Wyczyszczone' },
];

const SEVERITY_LABELS: Record<AlertSeverity, string> = {
  critical: 'Krytyczny',
  warning: 'Ostrzezenie',
  info: 'Info',
};

function SeverityBadge({ severity }: { severity: AlertSeverity }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_STYLES[severity]}`}>
      {SEVERITY_LABELS[severity]}
    </span>
  );
}

// ── Recipients Tab ──

function RecipientsTab() {
  const recipients = useAlertRecipients();
  const queryClient = useQueryClient();
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    setLoading(true);
    setError(null);
    try {
      await api.addAlertRecipient({ email, name: newName.trim(), groups: 'all' });
      setNewEmail('');
      setNewName('');
      queryClient.invalidateQueries({ queryKey: ['alert-recipients'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (r: AlertRecipient) => {
    try {
      await api.updateAlertRecipient(r.email, { isActive: !r.isActive });
      queryClient.invalidateQueries({ queryKey: ['alert-recipients'] });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRemove = async (email: string) => {
    if (!confirm(`Usunac odbiorce ${email}?`)) return;
    try {
      await api.removeAlertRecipient(email);
      queryClient.invalidateQueries({ queryKey: ['alert-recipients'] });
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          Dodaj odbiorce alertow
        </h2>
        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-500 mb-1">Email</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@cloudteam.pl"
              className="input-field w-full"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
          </div>
          <div className="min-w-[150px]">
            <label className="block text-xs text-gray-500 mb-1">Nazwa</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Jan Kowalski"
              className="input-field w-full"
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={loading || !newEmail.includes('@')}
            className="btn-primary disabled:opacity-50"
          >
            {loading ? '...' : 'Dodaj'}
          </button>
        </div>
        {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
      </div>

      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
          Odbiorcy alertow ({recipients.data?.length ?? 0})
        </h2>
        {recipients.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : recipients.error ? (
          <ErrorAlert message={recipients.error.message} onRetry={() => recipients.refetch()} />
        ) : recipients.data?.length === 0 ? (
          <p className="text-sm text-gray-500">Brak odbiorcow. Dodaj pierwszego odbiorce powyzej.</p>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {recipients.data?.map((r) => (
              <div key={r.email} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className={`p-1.5 rounded-full ${r.isActive ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                    <Users className={`h-4 w-4 ${r.isActive ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {r.name ? `${r.name} (${r.email})` : r.email}
                    </p>
                    <p className="text-xs text-gray-500">
                      Dodany przez {r.addedBy}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggle(r)}
                    className="p-1.5 text-gray-400 hover:text-green-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    title={r.isActive ? 'Dezaktywuj' : 'Aktywuj'}
                  >
                    {r.isActive ? <ToggleRight className="h-5 w-5 text-green-500" /> : <ToggleLeft className="h-5 w-5" />}
                  </button>
                  <button
                    onClick={() => handleRemove(r.email)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    title="Usun"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Rules Tab ──

function RulesTab() {
  const rules = useAlertRules();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [editingRule, setEditingRule] = useState<string | null>(null);
  const [checkResults, setCheckResults] = useState<AlertCheckResult[] | null>(null);
  const [checking, setChecking] = useState(false);

  const handleToggle = async (rule: AlertRule) => {
    try {
      await api.updateAlertRule(rule.ruleId, { isActive: !rule.isActive });
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateThreshold = async (rule: AlertRule, thresholdValue: number, thresholdWindow: number, cooldownMinutes: number) => {
    try {
      await api.updateAlertRule(rule.ruleId, { thresholdValue, thresholdWindow, cooldownMinutes });
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      setEditingRule(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleManualCheck = async () => {
    setChecking(true);
    setError(null);
    try {
      const results = await api.runAlertCheck();
      setCheckResults(results);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Reguly alertow ({rules.data?.length ?? 0})
        </h2>
        <button
          onClick={handleManualCheck}
          disabled={checking}
          className="btn-primary flex items-center gap-2 text-sm"
        >
          <Play className="h-4 w-4" />
          {checking ? 'Sprawdzam...' : 'Sprawdz teraz'}
        </button>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {checkResults && (
        <div className="card bg-gray-50 dark:bg-gray-800/50">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Wyniki sprawdzenia</h3>
          <div className="space-y-2">
            {checkResults.map((r) => (
              <div key={r.ruleId} className={`flex items-center gap-3 p-2 rounded-lg text-sm ${
                r.triggered ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'
              }`}>
                {r.triggered ? (
                  <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                ) : (
                  <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                )}
                <span className="text-gray-700 dark:text-gray-300">{r.message}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => setCheckResults(null)}
            className="mt-3 text-xs text-gray-500 hover:text-gray-700"
          >
            Zamknij wyniki
          </button>
        </div>
      )}

      {rules.isLoading ? (
        <LoadingSpinner size="sm" />
      ) : rules.error ? (
        <ErrorAlert message={rules.error.message} onRetry={() => rules.refetch()} />
      ) : (
        <div className="space-y-2">
          {rules.data?.map((rule) => (
            <div key={rule.ruleId} className="card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <button
                    onClick={() => handleToggle(rule)}
                    className="shrink-0"
                    title={rule.isActive ? 'Dezaktywuj' : 'Aktywuj'}
                  >
                    {rule.isActive ? (
                      <ToggleRight className="h-6 w-6 text-green-500" />
                    ) : (
                      <ToggleLeft className="h-6 w-6 text-gray-400" />
                    )}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{rule.name}</p>
                      <SeverityBadge severity={rule.severity} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Prog: {rule.thresholdValue}
                      {rule.thresholdWindow > 0 && ` · Okno: ${rule.thresholdWindow} min`}
                      {' · '}Cooldown: {rule.cooldownMinutes} min
                      {rule.lastTriggeredAt && ` · Ostatni alert: ${new Date(rule.lastTriggeredAt).toLocaleString('pl-PL')}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setEditingRule(editingRule === rule.ruleId ? null : rule.ruleId)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  {editingRule === rule.ruleId ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              </div>

              {editingRule === rule.ruleId && (
                <RuleEditor rule={rule} onSave={handleUpdateThreshold} onCancel={() => setEditingRule(null)} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RuleEditor({
  rule,
  onSave,
  onCancel,
}: {
  rule: AlertRule;
  onSave: (rule: AlertRule, threshold: number, window: number, cooldown: number) => void;
  onCancel: () => void;
}) {
  const [threshold, setThreshold] = useState(rule.thresholdValue);
  const [window, setWindow] = useState(rule.thresholdWindow);
  const [cooldown, setCooldown] = useState(rule.cooldownMinutes);

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <div className="flex gap-4 items-end flex-wrap">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Prog</label>
          <input
            type="number"
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            className="input-field w-24"
            min={0}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Okno (min)</label>
          <input
            type="number"
            value={window}
            onChange={(e) => setWindow(Number(e.target.value))}
            className="input-field w-24"
            min={0}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Cooldown (min)</label>
          <input
            type="number"
            value={cooldown}
            onChange={(e) => setCooldown(Number(e.target.value))}
            className="input-field w-24"
            min={1}
          />
        </div>
        <div className="flex gap-2">
          <button onClick={() => onSave(rule, threshold, window, cooldown)} className="btn-primary text-sm">
            Zapisz
          </button>
          <button onClick={onCancel} className="btn-secondary text-sm">
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SMTP Tab ──

function SmtpTab() {
  const smtpConfig = useSmtpConfig();
  const queryClient = useQueryClient();
  const [host, setHost] = useState('');
  const [port, setPort] = useState(587);
  const [secure, setSecure] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [fromName, setFromName] = useState('Credentium Alerts');
  const [region, setRegion] = useState('eu-west-1');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Pre-fill form from existing config
  useEffect(() => {
    if (smtpConfig.data && !initialized && 'host' in smtpConfig.data) {
      const cfg = smtpConfig.data;
      if (cfg.host) setHost(cfg.host);
      setPort(cfg.port || 587);
      setSecure(cfg.secure ?? true);
      if (cfg.username) setUsername(cfg.username);
      if (cfg.fromAddress) setFromAddress(cfg.fromAddress);
      if (cfg.fromName) setFromName(cfg.fromName);
      if (cfg.region) setRegion(cfg.region);
      setInitialized(true);
    }
  }, [smtpConfig.data, initialized]);

  const handleSave = async () => {
    if (!host || !fromAddress) {
      setError('Host i adres nadawcy sa wymagane');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await api.saveSmtpConfig({
        host, port, secure, username,
        ...(password ? { password } : {}),
        fromAddress, fromName, region,
      });
      queryClient.invalidateQueries({ queryKey: ['smtp-config'] });
      setSuccess('Konfiguracja SMTP zapisana');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.testSmtp();
      setSuccess(`Email testowy wyslany do: ${result.to}`);
    } catch (err: any) {
      setError(`Blad wysylki: ${err.message}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Konfiguracja SMTP (AWS SES)
        </h2>

        {smtpConfig.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Host SMTP</label>
                <input
                  type="text"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  placeholder="email-smtp.eu-west-1.amazonaws.com"
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Port</label>
                <input
                  type="number"
                  value={port}
                  onChange={(e) => setPort(Number(e.target.value))}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Region AWS</label>
                <select value={region} onChange={(e) => setRegion(e.target.value)} className="input-field w-full">
                  <option value="eu-west-1">eu-west-1 (Irlandia)</option>
                  <option value="eu-central-1">eu-central-1 (Frankfurt)</option>
                  <option value="us-east-1">us-east-1 (Virginia)</option>
                </select>
              </div>
              <div className="flex items-center gap-2 pt-5">
                <input
                  type="checkbox"
                  id="smtp-secure"
                  checked={secure}
                  onChange={(e) => setSecure(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="smtp-secure" className="text-sm text-gray-700 dark:text-gray-300">TLS/SSL</label>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Uzytkownik SMTP</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="SMTP IAM credentials"
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Haslo SMTP</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={initialized ? '(bez zmian)' : 'Haslo SMTP SES'}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Adres nadawcy (From)</label>
                <input
                  type="email"
                  value={fromAddress}
                  onChange={(e) => setFromAddress(e.target.value)}
                  placeholder="alerts@credentium.com"
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Nazwa nadawcy</label>
                <input
                  type="text"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder="Credentium Alerts"
                  className="input-field w-full"
                />
              </div>
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}
            {success && <p className="text-sm text-green-600">{success}</p>}

            <div className="flex gap-3">
              <button onClick={handleSave} disabled={loading} className="btn-primary flex items-center gap-2">
                {loading ? '...' : 'Zapisz konfiguracje'}
              </button>
              <button
                onClick={handleTest}
                disabled={testing || !(smtpConfig.data && 'isConfigured' in smtpConfig.data && smtpConfig.data.isConfigured)}
                className="btn-secondary flex items-center gap-2 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {testing ? 'Wysylam...' : 'Wyslij email testowy'}
              </button>
            </div>

            {smtpConfig.data && 'isConfigured' in smtpConfig.data && smtpConfig.data.isConfigured && (
              <p className="text-xs text-gray-400">
                Ostatnia aktualizacja: {smtpConfig.data.updatedAt ? new Date(smtpConfig.data.updatedAt).toLocaleString('pl-PL') : '-'}
                {' '}przez {smtpConfig.data.updatedBy || '-'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── History Tab ──

const HISTORY_PAGE_SIZE = 50;

function HistoryTab() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<AckStatus | 'all'>('all');
  const history = useAlertHistory(page, statusFilter, HISTORY_PAGE_SIZE);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Single shared mutation; only the row currently being mutated is disabled.
  const updateStatus = useUpdateAlertHistoryStatus();
  const pendingId = updateStatus.isPending ? updateStatus.variables?.id : undefined;

  const changeFilter = (value: AckStatus | 'all') => {
    setStatusFilter(value);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <History className="h-4 w-4" />
          Historia alertow
          {history.data && ` (${history.data.total})`}
        </h2>
        <div className="flex items-center gap-1 rounded-lg bg-gray-100 dark:bg-gray-800 p-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => changeFilter(f.value)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                statusFilter === f.value
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {history.isLoading ? (
        <LoadingSpinner size="sm" />
      ) : history.error ? (
        <ErrorAlert message={history.error.message} onRetry={() => history.refetch()} />
      ) : history.data?.entries.length === 0 ? (
        <div className="card">
          <p className="text-sm text-gray-500">
            {statusFilter === 'all' ? 'Brak wysylanych alertow.' : 'Brak alertow o wybranym statusie.'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {history.data?.entries.map((entry) => (
              <HistoryItem
                key={entry.id}
                entry={entry}
                expanded={expandedId === entry.id}
                onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                onUpdateStatus={(id, status) => updateStatus.mutate({ id, status })}
                pending={pendingId === entry.id}
              />
            ))}
          </div>

          {history.data && history.data.total > HISTORY_PAGE_SIZE && (
            <div className="flex items-center justify-center gap-4 pt-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary text-sm disabled:opacity-50"
              >
                Poprzednia
              </button>
              <span className="text-sm text-gray-500">
                Strona {page} z {Math.ceil(history.data.total / HISTORY_PAGE_SIZE)}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page * HISTORY_PAGE_SIZE >= history.data!.total}
                className="btn-secondary text-sm disabled:opacity-50"
              >
                Nastepna
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function HistoryItem({
  entry,
  expanded,
  onToggle,
  onUpdateStatus,
  pending,
}: {
  entry: AlertHistoryEntry;
  expanded: boolean;
  onToggle: () => void;
  onUpdateStatus: (id: string, status: 'acknowledged' | 'cleared') => void;
  pending: boolean;
}) {
  let details: Record<string, unknown> = {};
  try {
    details = JSON.parse(entry.details);
  } catch { /* ignore */ }

  const statusMeta = ACK_STATUS_META[entry.ackStatus];

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-3 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center gap-3 min-w-0">
          {entry.sendStatus === 'sent' ? (
            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
          ) : (
            <XCircle className="h-4 w-4 text-red-500 shrink-0" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{entry.ruleName}</p>
              <SeverityBadge severity={entry.severity} />
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusMeta.badge}`}>
                {statusMeta.label}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {new Date(entry.sentAt).toLocaleString('pl-PL')} · {entry.recipients}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {entry.ackStatus === 'new' && (
            <button
              onClick={(e) => { e.stopPropagation(); onUpdateStatus(entry.id, 'acknowledged'); }}
              disabled={pending}
              title="Potwierdz"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/40 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              Potwierdz
            </button>
          )}
          {entry.ackStatus !== 'cleared' && (
            <button
              onClick={(e) => { e.stopPropagation(); onUpdateStatus(entry.id, 'cleared'); }}
              disabled={pending}
              title="Wyczysc"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700/40 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <Eraser className="h-3.5 w-3.5" />
              Wyczysc
            </button>
          )}
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
          <p className="text-sm text-gray-700 dark:text-gray-300">{entry.message}</p>
          {Object.keys(details).length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
              {Object.entries(details).map(([key, value]) => (
                <div key={key} className="flex justify-between text-xs py-0.5">
                  <span className="text-gray-500">{key}</span>
                  <span className="text-gray-700 dark:text-gray-300 font-mono">{String(value)}</span>
                </div>
              ))}
            </div>
          )}
          {entry.sendStatus === 'failed' && entry.sendError && (
            <p className="text-xs text-red-500">Blad: {entry.sendError}</p>
          )}
          {entry.ackedBy && entry.ackStatus !== 'new' && (
            <p className="text-xs text-gray-400">
              {entry.ackStatus === 'cleared' ? 'Wyczyszczony' : 'Potwierdzony'} przez {entry.ackedBy}
              {entry.ackedAt && ` · ${new Date(entry.ackedAt).toLocaleString('pl-PL')}`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ──

export function AlertingPage() {
  const [activeTab, setActiveTab] = useState<Tab>('rules');
  const currentUser = useCurrentUser();

  const isAdmin = currentUser.data?.isAdmin ?? false;

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Brak dostepu</p>
      </div>
    );
  }

  const tabs: { id: Tab; label: string; icon: typeof Bell }[] = [
    { id: 'rules', label: 'Reguly', icon: AlertTriangle },
    { id: 'recipients', label: 'Odbiorcy', icon: Users },
    { id: 'smtp', label: 'SMTP', icon: Settings },
    { id: 'history', label: 'Historia', icon: History },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Bell className="h-6 w-6 text-brand-400" />
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Alerty</h1>
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          Monitorowane srodowisko: Production
        </span>
      </div>

      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-800">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'rules' && <RulesTab />}
      {activeTab === 'recipients' && <RecipientsTab />}
      {activeTab === 'smtp' && <SmtpTab />}
      {activeTab === 'history' && <HistoryTab />}
    </div>
  );
}
